<template>
  <div class="schedule-block">
    <div class="schedule-fields">
      <div class="field-group">
        <label class="schedule-label">
          {{ $t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.SCHEDULE_LABEL') }}
        </label>
        <select
          class="schedule-control"
          :value="schedule.mode"
          @change="updateSchedule({ mode: $event.target.value })"
        >
          <option value="immediate">
            {{ $t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.SCHEDULE_IMMEDIATE') }}
          </option>
          <option value="hours_after">
            {{ $t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.SCHEDULE_HOURS_AFTER') }}
          </option>
          <option value="fixed_time">
            {{ $t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.SCHEDULE_FIXED_TIME') }}
          </option>
        </select>
      </div>

      <div v-if="schedule.mode === 'hours_after'" class="field-group field-group--hours">
        <label class="schedule-label">
          {{ $t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.DELAY_LABEL') }}
        </label>
        <div class="hours-field">
          <input
            class="schedule-control hours-field__input"
            type="number"
            :min="hourBounds.MIN"
            :max="hourBounds.MAX"
            step="1"
            :value="schedule.hours"
            @input="updateSchedule({ hours: $event.target.value })"
          />
          <span class="hours-field__suffix">
            {{ hoursSuffix }}
          </span>
        </div>
      </div>

      <template v-if="schedule.mode === 'fixed_time'">
        <div class="field-group field-group--hours">
          <label class="schedule-label">
            {{ $t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.DELAY_LABEL') }}
          </label>
          <div class="hours-field">
            <input
              class="schedule-control hours-field__input"
              type="number"
              :min="dayBounds.MIN"
              :max="dayBounds.MAX"
              step="1"
              :value="schedule.days"
              @input="updateSchedule({ days: $event.target.value })"
            />
            <span class="hours-field__suffix">
              {{ daysSuffix }}
            </span>
          </div>
        </div>

        <div class="field-group">
          <label class="schedule-label">
            {{ $t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.TIME_LABEL') }}
          </label>
          <input
            class="schedule-control"
            type="time"
            step="60"
            :value="schedule.time"
            @input="updateSchedule({ time: $event.target.value })"
          />
        </div>
      </template>
    </div>

    <p v-if="preview" class="schedule-preview">{{ preview }}</p>
    <p v-if="errorMessage" class="schedule-error">{{ errorMessage }}</p>
  </div>
</template>

<script>
import {
  AI_EMAIL_DELAY_DAYS,
  AI_EMAIL_DELAY_HOURS,
  aiEmailErrorBounds,
  aiEmailScheduleErrorCode,
  formatClockTime,
  normalizeAiEmailSchedule,
} from 'dashboard/helper/aiEmailSchedule';

const ERROR_KEYS = {
  hours: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_HOURS',
  days: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_DAYS',
  time: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_TIME',
  past: 'AUTOMATION.ACTION.AI_GENERATED_EMAIL.ERROR_PAST',
};

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      hourBounds: AI_EMAIL_DELAY_HOURS,
      dayBounds: AI_EMAIL_DELAY_DAYS,
    };
  },
  computed: {
    schedule() {
      const normalized = normalizeAiEmailSchedule(this.value);
      const raw = this.value || {};
      return {
        ...normalized,
        hours:
          raw.hours === undefined || raw.hours === null
            ? normalized.hours
            : raw.hours,
        days:
          raw.days === undefined || raw.days === null
            ? normalized.days
            : raw.days,
        time:
          raw.time === undefined || raw.time === null
            ? normalized.time
            : String(raw.time).slice(0, 5),
      };
    },
    hoursSuffix() {
      return Number(this.schedule.hours) === 1
        ? this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.HOUR_AFTER')
        : this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.HOURS_AFTER');
    },
    daysSuffix() {
      return Number(this.schedule.days) === 1
        ? this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.DAY_AFTER')
        : this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.DAYS_AFTER');
    },
    preview() {
      const schedule = this.schedule;
      if (schedule.mode === 'hours_after') {
        const hours = Number(schedule.hours);
        if (!Number.isInteger(hours) || hours < 1) return '';
        return hours === 1
          ? this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.PREVIEW_HOURS_ONE')
          : this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.PREVIEW_HOURS_OTHER', {
              count: hours,
            });
      }
      if (schedule.mode === 'fixed_time') {
        const days = Number(schedule.days);
        const timeLabel = formatClockTime(schedule.time);
        if (!Number.isInteger(days) || days < 1 || !timeLabel) return '';
        return days === 1
          ? this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.PREVIEW_DAYS_ONE', {
              time: timeLabel,
            })
          : this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.PREVIEW_DAYS_OTHER', {
              count: days,
              time: timeLabel,
            });
      }
      return this.$t('AUTOMATION.ACTION.AI_GENERATED_EMAIL.PREVIEW_IMMEDIATE');
    },
    errorMessage() {
      const code = aiEmailScheduleErrorCode(this.schedule);
      if (!code || !ERROR_KEYS[code]) return '';
      const bounds = aiEmailErrorBounds(code);
      if (code === 'hours' || code === 'days') {
        return this.$t(ERROR_KEYS[code], {
          min: bounds.MIN,
          max: bounds.MAX,
        });
      }
      return this.$t(ERROR_KEYS[code]);
    },
  },
  methods: {
    updateSchedule(patch) {
      const next = normalizeAiEmailSchedule({
        ...this.schedule,
        ...patch,
      });
      if (patch.hours !== undefined) next.hours = patch.hours;
      if (patch.days !== undefined) next.days = patch.days;
      if (patch.time !== undefined) next.time = String(patch.time || '').slice(0, 5);
      this.$emit('input', next);
    },
  },
};
</script>

<style scoped lang="scss">
.schedule-block {
  @apply flex w-full flex-col gap-2;
}

.schedule-fields {
  @apply flex w-full flex-wrap items-end gap-3;
}

.field-group {
  @apply flex flex-col min-w-[10rem] flex-1;
}

.field-group--hours {
  @apply min-w-[14rem] flex-[1.4];
}

.schedule-label {
  @apply mb-1 text-xs font-medium text-slate-600 dark:text-slate-300;
}

.schedule-control {
  @apply mb-0 h-10 w-full rounded border border-solid border-slate-200 bg-white px-2 text-sm text-slate-800
    dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100;
}

.hours-field {
  @apply flex h-10 items-center gap-2;
}

.hours-field__input {
  @apply w-24 shrink-0;
}

.hours-field__suffix {
  @apply text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap;
}

.schedule-preview {
  @apply mb-0 text-xs text-slate-500 dark:text-slate-400;
}

.schedule-error {
  @apply mb-0 text-xs text-red-500 dark:text-red-400;
}
</style>
